import { memo } from "react";

import { RadioGroup } from "@headlessui/react";
import {
  type Maybe,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import { OptionSelector } from "@components/VariantSelector/OptionSelector.view";
import {
  type CartProductVariant,
  type CartRenderVariantOption,
} from "@tablescape/state";
import { tw } from "classnames";

type Props = {
  context?: "productForm" | "productSelector" | "cart";
  optionCategories?: Maybe<string[]>;
  productName?: string;
  renderOption: CartRenderVariantOption;
  selected: CartProductVariant | undefined;
  selectedOption: SelectedOption;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  type: "artwork" | "color" | "style" | "generic";
  variants: CartProductVariant[];
};

const styles = {
  content: tw("flex", "flex-col", "gap-2", "w-full"),
  categoriesContainer: tw("flex", "flex-col", "gap-y-6"),
  categoryContainer: tw("flex", "flex-col", "gap-y-2"),
  image: (size: "tiny" | "mini" | "regular" | "small" | "large") =>
    size !== "regular" && size !== "large"
      ? tw("h-16", "w-16", "md:h-18", "md:w-18")
      : "",
  screen: tw("sr-only"),
};

const VariantSelectorViewBase = ({
  context,
  optionCategories,
  productName,
  renderOption,
  selected,
  selectedOption,
  setSelected,
  showProductName,
  type,
  variants,
}: Props) => {
  return (
    <RadioGroup onChange={setSelected} value={selected?.id}>
      <RadioGroup.Label className={styles.screen}>
        Product {selectedOption.name}
      </RadioGroup.Label>
      <div className={styles.content}>
        {optionCategories && context === "productForm" ? (
          <div className={styles.categoriesContainer}>
            {optionCategories.map((optionCategory: string) => (
              <div
                className={styles.categoryContainer}
                key={`radioGroup-${optionCategory}`}
              >
                <OptionSelector
                  context={context}
                  name={optionCategory}
                  renderOption={renderOption}
                  selected={selected}
                  type={type}
                  value={selectedOption.value}
                  variants={variants}
                />
              </div>
            ))}
          </div>
        ) : (
          <OptionSelector
            context={context}
            name={selectedOption.name}
            productName={productName}
            renderOption={renderOption}
            selected={selected}
            showProductName={showProductName}
            type={type}
            value={selectedOption.value}
            variants={variants}
          />
        )}
      </div>
    </RadioGroup>
  );
};

export const VariantSelectorView = memo(VariantSelectorViewBase);
